import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import { base_url } from "../utils/axiosConfig";
import { useDispatch } from "react-redux";
import { registerUser } from "../features/user/userSlice";

// Debounce function to prevent rapid API calls
const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

// Check Email Exists API
const checkEmailExists = async (email) => {
  try {
    const res = await axios.get(`${base_url}user/all-users`);
    return res.data.some((u) => u.email.toLowerCase() === email.toLowerCase());
  } catch (error) {
    console.error("Fetch error:", error);
    return false;
  }
};

const Signup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [emailExists, setEmailExists] = useState(false);
  const [timer, setTimer] = useState(600); // 10 minutes

  const timerRef = useRef(null);

  // Start OTP Timer
  const startOtpTimer = () => {
    setTimer(600);
    clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Debounced API call for checking email
  const debouncedEmailCheck = useRef(
    debounce(async (value) => {
      const exists = await checkEmailExists(value);
      setEmailExists(exists);
    }, 600)
  ).current;

  // Formik Setup
  const formik = useFormik({
    initialValues: {
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: yup.object({
      email: yup.string().email("Invalid email").required("Email is required*"),
      phoneNumber: yup.string().required("Phone Number required*"),
      password: yup.string().required("Password required*"),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref("password"), null], "Passwords do not match")
        .required("Confirm Password required*"),
    }),

    onSubmit: async (values) => {
      try {
        if (emailExists) {
          toast.error("Email already registered!");
          return;
        }

        setLoading(true);

        const result = await dispatch(registerUser(values)).unwrap();

        if (result?.userId) {
          setUserId(result.userId);
          setStep(2);
          toast.success("OTP sent successfully!");
          startOtpTimer(); // Start countdown
        }
      } catch (error) {
        toast.error(error || "Registration failed");
      } finally {
        setLoading(false);
      }
    },
  });

  // Trigger email validation API ONLY when typing email
  useEffect(() => {
    if (formik.values.email) {
      debouncedEmailCheck(formik.values.email);
    }
  }, [formik.values.email]);

  // OTP input handler
  const handleOtpChange = (val, idx) => {
    if (/^\d?$/.test(val)) {
      const newArr = [...otp];
      newArr[idx] = val;
      setOtp(newArr);

      if (val && idx < 5) {
        document.getElementById(`otp-${idx + 1}`).focus();
      }
    }
  };

  // OTP Submit
  const handleOtpSubmit = async () => {
    if (timer === 0) {
      toast.error("OTP expired! Please request again.");
      return;
    }

    const code = otp.join("");

    if (code.length !== 6) {
      toast.error("Enter a valid 6-digit OTP");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(`${base_url}user/verify-otp`, {
        userId,
        otp: code,
      });

      if (res.status === 200) {
        toast.success("OTP Verified! Signup Complete. Please Login to continue.");
        navigate("/login");
      } else {
        toast.error("Invalid OTP");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "OTP Verification Failed");
    } finally {
      setLoading(false);
    }
  };

  // Format timer mm:ss
  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div
      className="py-5"
      style={{
        background: "linear-gradient(to bottom, #e8f5e9, #f1f8e9)",
        minHeight: "100vh",
      }}
    >
      <Toaster position="top-center" />

      <div className="container d-flex justify-content-center align-items-center">
        <div
          className="shadow p-4 p-md-2 rounded-4 w-100"
          style={{
            maxWidth: "420px",
            background: "#ffffff",
            borderRadius: "25px",
          }}
        >
          {/* BACK */}
          <button
            onClick={() => navigate(-1)}
            className="btn btn-success rounded-pill shadow-sm position-absolute top-0 start-0 m-3 d-flex align-items-center gap-2 px-3 py-2"
          >
            <IoArrowBack size={18} />
            Back
          </button>


          {/* HEADER */}
          <div className="text-center">
            <img
              src="/grocery-basket.png"
              alt=""
              style={{ width: "70px", marginBottom: "10px" }}
            />
            <h3 className="fw-bold" style={{ color: "#1b5e20" }}>
              {step === 1 ? "Create Account" : "Verify OTP"}
            </h3>
            <p className="text-muted small">
              {step === 1
                ? "Join our grocery family & start shopping!"
                : "Enter the OTP sent to your mobile"}
            </p>
          </div>

          {/* STEP 1 */}
          {step === 1 && (
            <form onSubmit={formik.handleSubmit} className="mt-4 p-3 d-flex flex-column gap-3">

              {/* EMAIL */}
              <div className="form-floating">
                <input
                  className="form-control px-4"
                  style={{ height: "55px" }}
                  type="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <label>Email Address</label>
                {emailExists && <small className="text-danger ms-2">Email already exists!</small>}
                {formik.touched.email && formik.errors.email && (
                  <small className="text-danger ms-2">{formik.errors.email}</small>
                )}
              </div>

              {/* PHONE */}
              <div className="form-floating">
                <input
                  className="form-control px-4"
                  style={{ height: "55px" }}
                  type="number"
                  name="phoneNumber"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <label>Phone Number</label>
                {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                  <small className="text-danger ms-2">{formik.errors.phoneNumber}</small>
                )}
              </div>

              {/* PASSWORD */}
              <div className="form-floating">
                <input
                  className="form-control px-4"
                  style={{ height: "55px" }}
                  type="password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <label>Password</label>
                {formik.touched.password && formik.errors.password && (
                  <small className="text-danger ms-2">{formik.errors.password}</small>
                )}
              </div>

              {/* CONFIRM PASSWORD */}
              <div className="form-floating">
                <input
                  className="form-control px-4"
                  style={{ height: "55px" }}
                  type="password"
                  name="confirmPassword"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <label>Confirm Password</label>
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <small className="text-danger ms-2">{formik.errors.confirmPassword}</small>
                )}
              </div>

              {/* BUTTON */}
              <button
                className="btn w-100 mt-3 rounded-pill"
                style={{
                  background: "#43a047",
                  color: "white",
                  height: "50px",
                  fontWeight: "600",
                  fontSize: "1rem",
                }}
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>Sending OTP...
                  </>
                ) : (
                  "Send OTP"
                )}
              </button>

              <p className="text-center mt-3 small">
                Already have an account?{" "}
                <Link to="/login" className="fw-semibold" style={{ color: "#2e7d32" }}>
                  Login
                </Link>
              </p>
            </form>
          )}

          {/* STEP 2 - OTP */}
          {step === 2 && (
            <div className="text-center mt-4">

              {/* TIMER */}
              <p className="fw-semibold" style={{ color: "#d32f2f" }}>
                Time Remaining: {formatTime(timer)}
              </p>

              <div className="d-flex justify-content-center gap-2 mt-3 mb-4">
                {otp.map((val, i) => (
                  <input
                    key={i}
                    id={`otp-${i}`}
                    type="text"
                    maxLength={1}
                    disabled={timer === 0}
                    className="form-control text-center rounded-3 shadow-sm"
                    style={{
                      width: "48px",
                      height: "58px",
                      fontSize: "1.6rem",
                      fontWeight: "700",
                      border: "2px solid #c8e6c9",
                    }}
                    value={val}
                    onChange={(e) => handleOtpChange(e.target.value, i)}
                  />
                ))}
              </div>

              <button
                className="btn w-100 rounded-pill"
                style={{
                  background: "#2e7d32",
                  color: "white",
                  height: "50px",
                  fontWeight: "600",
                }}
                onClick={handleOtpSubmit}
                disabled={loading || timer === 0}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>Verifying...
                  </>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>

  );
};

export default Signup;
